import { useMemo, useState } from 'react';
import { Flex, Icon, Label, Text, TextInput } from '@gravity-ui/uikit';
import { ChevronDown, ChevronUp, Magnifier } from '@gravity-ui/icons';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoOrganizationSelectedState,
} from '@/shared/ui/states';
import { HighlightText } from '@/shared/ui/highlight-text';
import { useApi } from '@/shared/api/use-api';
import { useBatchExpiry, useOpenBatches } from '@/features/warehouse/api';
import { formatDate, formatMoney, formatQuantity, lineTotal } from '@/features/warehouse/format';
import { ObjectSelect, useObjectChoice } from '@/features/warehouse/object-select';
import type { Batch } from '@/features/warehouse/types';
import { useTranslation } from '@/shared/i18n';
import {
  EXPIRY_WARNING_WORKING_DAYS,
  addWorkingDays,
  expiryStatus,
  todayIso,
  type ExpiryStatus,
} from './expiry';
import './warehouse-balances-route.css';

/** Карточка продукта: открытые партии и итог по ним. */
interface ProductCard {
  productId: string;
  productName: string;
  unit: string;
  batches: Batch[];
  /** Средняя цена остатка, взвешенная по количеству. */
  avgPrice: number;
  totalRemaining: number;
  /** Сумма остатка, ₽: остаток каждой партии по её цене. */
  totalValue: number;
}

/** Группирует партии по продукту; поиск — по названию продукта и накладной. */
function buildCards(batches: Batch[], search: string): ProductCard[] {
  const query = search.trim().toLowerCase();
  const byProduct = new Map<string, ProductCard>();

  for (const batch of batches) {
    if (
      query &&
      !batch.productName.toLowerCase().includes(query) &&
      !batch.invoiceNumber.toLowerCase().includes(query)
    ) {
      continue;
    }

    let card = byProduct.get(batch.productId);
    if (!card) {
      card = {
        productId: batch.productId,
        productName: batch.productName,
        unit: batch.unit,
        batches: [],
        avgPrice: 0,
        totalRemaining: 0,
        totalValue: 0,
      };
      byProduct.set(batch.productId, card);
    }
    card.batches.push(batch);
  }

  const cards = [...byProduct.values()];
  for (const card of cards) {
    const weighted = card.batches.reduce((sum, b) => sum + b.unitPrice * b.remaining, 0);
    card.totalRemaining = card.batches.reduce((sum, b) => sum + b.remaining, 0);
    card.avgPrice = card.totalRemaining > 0 ? weighted / card.totalRemaining : 0;
    card.totalValue = card.batches.reduce((sum, b) => sum + lineTotal(b.remaining, b.unitPrice), 0);
  }

  return cards.sort((a, b) => a.productName.localeCompare(b.productName, 'ru'));
}

/** Худший статус партий товара: просрочка важнее «скоро истекает». */
function worstStatus(statuses: (ExpiryStatus | null)[]): ExpiryStatus | null {
  if (statuses.includes('expired')) return 'expired';
  if (statuses.includes('soon')) return 'soon';
  return null;
}

/** Срок годности партии с пометкой, если он вышел или выходит скоро. */
function ExpiryCell({ date, status }: { date: string; status: ExpiryStatus | null }) {
  const { t } = useTranslation();
  if (!date) return <>—</>;

  return (
    <Flex alignItems="center" gap={2} wrap>
      <span style={{ whiteSpace: 'nowrap' }}>{formatDate(date)}</span>
      {status && (
        <Label size="xs" theme={status === 'expired' ? 'danger' : 'warning'}>
          {status === 'expired' ? t('balances.expired') : t('balances.expiresSoon')}
        </Label>
      )}
    </Flex>
  );
}

/** Раздел «Склад → Остатки»: открытые партии объекта в разрезе продуктов. */
export function WarehouseBalancesRoute() {
  const api = useApi();
  const { t } = useTranslation();
  const [objectId, setObjectId] = useState('');
  const { options, objects, changeObjectId } = useObjectChoice(objectId, setObjectId);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const batches = useOpenBatches(objectId);
  const cards = useMemo(
    () => buildCards(batches.data?.batches ?? [], search),
    [batches.data, search]
  );

  const expiry = useBatchExpiry(batches.data?.batches);
  const today = todayIso();
  const deadline = addWorkingDays(today, EXPIRY_WARNING_WORKING_DAYS);
  const expiryOf = (batch: Batch) => expiry.data?.[batch.id] ?? '';
  const statusOf = (batch: Batch) => expiryStatus(expiryOf(batch), today, deadline);

  const toggleExpanded = (productId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  if (api.organizationId === null) {
    return (
      <Flex direction="column" className="warehouse-balances-page">
        <NoOrganizationSelectedState />
      </Flex>
    );
  }

  const noObjects = objects.isSuccess && options.length === 0;

  return (
    <div className="warehouse-balances-page">
      <Flex justifyContent="space-between" gap={8} className="warehouse-balances-page__toolbar">
        <ObjectSelect
          value={objectId}
          onUpdate={changeObjectId}
          options={options}
          disabled={objects.isPending}
        />
        <TextInput
          size="l"
          placeholder={t('balances.searchPlaceholder')}
          value={search}
          onUpdate={setSearch}
          hasClear
          startContent={
            <span style={{ display: 'inline-flex', paddingInlineStart: 4, paddingInlineEnd: 4 }}>
              <Icon data={Magnifier} size={16} />
            </span>
          }
          style={{ width: 320 }}
        />
      </Flex>

      {objects.isError && (
        <ErrorState error={objects.error} onRetry={() => void objects.refetch()} />
      )}
      {noObjects && <EmptyState text={t('balances.emptyNoObjects')} />}
      {!noObjects && !objects.isError && (objects.isPending || batches.isPending) && (
        <LoadingState />
      )}
      {batches.isError && (
        <ErrorState error={batches.error} onRetry={() => void batches.refetch()} />
      )}
      {expiry.isError && (
        <Text variant="body-1" color="danger">
          {t('balances.expiryLoadError')}
        </Text>
      )}
      {batches.isSuccess && cards.length === 0 && (
        <EmptyState text={search.trim() ? t('balances.emptySearch') : t('balances.emptyNone')} />
      )}

      {batches.isSuccess && cards.length > 0 && (
        <div className="warehouse-balances-table">
          <div className="warehouse-balances-table__header">
            <table className="admin-table">
              <colgroup>
                <col />
                <col className="warehouse-balances-col-price" />
                <col className="warehouse-balances-col-rest" />
                <col className="warehouse-balances-col-sum" />
                <col className="warehouse-balances-col-chevron" />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('balances.colProduct')}</th>
                  <th className="warehouse-balances-col-price">{t('balances.colAvgPrice')}</th>
                  <th className="warehouse-balances-col-rest">{t('balances.colRemaining')}</th>
                  <th className="warehouse-balances-col-sum">{t('balances.colSum')}</th>
                  <th className="warehouse-balances-col-chevron" />
                </tr>
              </thead>
            </table>
          </div>

          <div className="warehouse-balances-table__scroll">
            <div className="admin-table-wrap">
              <table className="admin-table warehouse-balances-table__body">
                <tbody>
                  {cards.map((card) => {
                    const open = expanded.has(card.productId);
                    const cardStatus = worstStatus(card.batches.map(statusOf));
                    return (
                      <tr
                        key={card.productId}
                        className={
                          open ? 'warehouse-balances-card is-open' : 'warehouse-balances-card'
                        }
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (!open) toggleExpanded(card.productId);
                        }}
                        onKeyDown={(event) => {
                          if ((event.key === 'Enter' || event.key === ' ') && !open) {
                            toggleExpanded(card.productId);
                          }
                        }}
                        title={open ? t('receipt.collapse') : t('receipt.expand')}
                        aria-label={
                          open ? t('receipt.collapseProduct') : t('receipt.expandProduct')
                        }
                        aria-expanded={open}
                      >
                        <td colSpan={5}>
                          <div className="warehouse-balances-card__summary">
                            <div className="warehouse-balances-card__name">
                              <Text variant="body-2" ellipsis>
                                <HighlightText text={card.productName} query={search} />
                              </Text>
                              {cardStatus && (
                                <Label
                                  size="xs"
                                  theme={cardStatus === 'expired' ? 'danger' : 'warning'}
                                  className="warehouse-balances-card__expiry"
                                >
                                  {cardStatus === 'expired'
                                    ? t('balances.expired')
                                    : t('balances.expiresSoon')}
                                </Label>
                              )}
                            </div>
                            <div className="warehouse-balances-card__price">
                              <Text variant="body-2">{formatMoney(card.avgPrice)}</Text>
                            </div>
                            <div className="warehouse-balances-card__rest">
                              <Text variant="body-2">
                                {formatQuantity(card.totalRemaining, card.unit)}
                              </Text>
                            </div>
                            <div className="warehouse-balances-card__sum">
                              <Text variant="body-2">{formatMoney(card.totalValue)}</Text>
                            </div>
                            <div
                              className="warehouse-balances-card__chevron"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpanded(card.productId);
                              }}
                              title={open ? t('receipt.collapse') : t('receipt.expand')}
                            >
                              <Icon data={open ? ChevronUp : ChevronDown} size={16} />
                            </div>
                          </div>

                          {open && (
                            <div className="warehouse-balances-card__batches">
                              <table className="warehouse-balances-batches">
                                <thead>
                                  <tr>
                                    <th>{t('balances.colInvoice')}</th>
                                    <th>{t('balances.colDate')}</th>
                                    <th>{t('balances.colExpiry')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('balances.colPrice')}</th>
                                    <th style={{ textAlign: 'right' }}>
                                      {t('balances.colReceived')}
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                      {t('balances.colRemaining')}
                                    </th>
                                    <th style={{ textAlign: 'right' }}>{t('balances.colSum')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {card.batches.map((batch) => (
                                    <tr key={batch.id}>
                                      <td>
                                        <HighlightText text={batch.invoiceNumber} query={search} />
                                      </td>
                                      <td>{formatDate(batch.receiptDate)}</td>
                                      <td>
                                        <ExpiryCell
                                          date={expiryOf(batch)}
                                          status={statusOf(batch)}
                                        />
                                      </td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {formatMoney(batch.unitPrice)}
                                      </td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {formatQuantity(batch.quantity, card.unit)}
                                      </td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {formatQuantity(batch.remaining, card.unit)}
                                      </td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {formatMoney(lineTotal(batch.remaining, batch.unitPrice))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="warehouse-balances-table__total">
            <Text variant="body-2" color="secondary">
              {t('balances.totalValue')}
            </Text>
            <Text variant="subheader-2">
              {formatMoney(cards.reduce((sum, card) => sum + card.totalValue, 0))}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
